import { Fragment, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axiosAdmin from "../../../../../api/apiAdmin";
import { successToast, errorToast } from "../../../toastAlerts/ToastAlerts";
import { apiError } from "../../../../../api/apiError";

/**
 * Modal for inspecting and controlling a pack's price.
 * - lists every recalculation entry written by App\Services\PriceCalculator
 * - lets an admin lock the price (pack-only, or for the whole user+shipment group)
 * - lets an admin trigger a manual recalculation, optionally overriding per-kg USD
 *
 * Props:
 *   pack: the pack object as returned by PackResource (must include id, transport_price,
 *         price_locked, price_lock_scope, price_lock_amount, price_lock_reason, user_id,
 *         shipment_code).
 *   onClose: () => void
 *   onUpdated?: (freshPack) => void — fired after lock/unlock/recalc so the parent can refresh.
 */
const PriceAuditModal = ({ pack, onClose, onUpdated }) => {
    const { t } = useTranslation();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [expanded, setExpanded] = useState(null);
    // Sibling parcels and the customs reasoning — both explain a price row that
    // otherwise looks like it changed on its own.
    const [ctx, setCtx] = useState(null);
    // Loaded on demand: finding it is a full scan of the request log.
    const [borderHistory, setBorderHistory] = useState(null);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const [lockAmount, setLockAmount] = useState(
        pack.price_lock_amount ?? pack.transport_price ?? ""
    );
    const [lockScope, setLockScope] = useState(pack.price_lock_scope || "pack");
    const [lockReason, setLockReason] = useState(pack.price_lock_reason || "");
    const [overrideKg, setOverrideKg] = useState("");
    const [recalcGroup, setRecalcGroup] = useState(false);

    const fetchLogs = useCallback(
        async (p = 1) => {
            setLoading(true);
            try {
                const { data } = await axiosAdmin.get(
                    `/packs/${pack.id}/priceLogs?per_page=15&page=${p}`
                );
                setLogs(p === 1 ? data.data : [...logs, ...data.data]);
                setHasMore(p < data.last_page);
                setPage(p);
            } catch (e) {
                errorToast(apiError(e, t("admin_failed_to_load") || "Failed to load"));
            }
            setLoading(false);
        },
        [pack.id, logs, t]
    );

    useEffect(() => {
        fetchLogs(1);
        axiosAdmin
            .get(`/packs/${pack.id}/context`)
            .then(({ data }) => setCtx(data))
            .catch(() => setCtx(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pack.id]);

    const handleLock = async () => {
        if (!lockAmount || isNaN(Number(lockAmount))) {
            errorToast("Enter a valid amount");
            return;
        }
        try {
            const { data } = await axiosAdmin.post(
                `/packs/${pack.id}/lockPrice`,
                {
                    amount: Number(lockAmount),
                    scope: lockScope,
                    reason: lockReason || null,
                }
            );
            successToast("price locked");
            onUpdated?.(data.pack);
            fetchLogs(1);
        } catch (e) {
            errorToast(apiError(e, e?.response?.data?.error || "Lock failed"));
        }
    };

    const handleUnlock = async () => {
        try {
            const { data } = await axiosAdmin.post(
                `/packs/${pack.id}/unlockPrice`,
                { recalculate: true }
            );
            successToast("price unlocked");
            onUpdated?.(data.pack);
            fetchLogs(1);
        } catch (e) {
            errorToast(apiError(e, "Unlock failed"));
        }
    };

    const handleRecalculate = async () => {
        try {
            const body = {};
            if (overrideKg && !isNaN(Number(overrideKg))) {
                body.override_per_kg_usd = Number(overrideKg);
            }
            if (recalcGroup) body.group = true;
            await axiosAdmin.post(`/packs/${pack.id}/recalculate`, body);
            successToast("recalculated");
            onUpdated?.();
            fetchLogs(1);
        } catch (e) {
            errorToast(apiError(e, "Recalc failed"));
        }
    };

    const fmt = (n) =>
        n === null || n === undefined ? "—" : Number(n).toFixed(2);
    const fmtDate = (d) =>
        d ? new Date(d).toLocaleString() : "—";

    // Most recent applied (non-skipped) recalc — its breakdown is what produced the
    // current transport_price. Skipped entries (locked, already_paid, no_increase_capped)
    // never wrote a new price, so we look past them.
    const latestApplied = logs.find(
        (l) => !l.skipped && l.breakdown && Object.keys(l.breakdown).length > 0
    );

    // Cap that prevented an increase, if any — surfaced separately so the admin sees
    // "would have been X but kept at Y".
    const lastCapped = logs.find(
        (l) => l.skipped && l.skip_reason === "no_increase_capped"
    );

    const final = latestApplied
        ? Number(latestApplied.breakdown.final ?? pack.transport_price ?? 0)
        : Number(pack.transport_price ?? 0);
    const pctOf = (v) =>
        !final || final === 0
            ? "—"
            : ((Number(v) / final) * 100).toFixed(1) + "%";
    const signed = (v, isCut) => {
        const n = Number(v);
        if (!n) return "0.00";
        return (isCut ? "−" : "+") + n.toFixed(2);
    };

    return (
        <div
            className="fixed inset-0 bg-black/60 z-[1000] flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-slate-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-auto p-4 scrollbar"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center border-b border-slate-300 dark:border-slate-600 pb-2 mb-3">
                    <h2 className="text-lg font-bold dark:text-white">
                        {t("admin_price_history") || "Price history & lock"} · #{pack.id} ·{" "}
                        {pack.tracking_number}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-slate-500 hover:text-red-500 text-xl px-2"
                    >
                        ×
                    </button>
                </div>

                {/* Why customs flagged it. The rule reads the whole user+flight
                    group, so a parcel can be red on account of its siblings —
                    the numbers are shown against the limits they crossed. */}
                {ctx?.border && (
                    <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 mb-3">
                        <p className="text-sm font-bold dark:text-white mb-2">
                            განბაჟება:{" "}
                            <span
                                className={
                                    ctx.border.current === 1
                                        ? "text-red-500"
                                        : ctx.border.current === 3
                                        ? "text-yellow-500"
                                        : ctx.border.current === 2
                                        ? "text-green-600"
                                        : "text-slate-400"
                                }
                            >
                                {ctx.border.label}
                            </span>
                        </p>

                        <div className="flex flex-col gap-1">
                            {ctx.border.reasons.map((r) => (
                                <div
                                    key={r.code}
                                    className={`text-xs flex items-center gap-2 ${
                                        r.met
                                            ? "text-red-600 font-bold"
                                            : "text-slate-400"
                                    }`}
                                >
                                    <span>{r.met ? "✔" : "—"}</span>
                                    <span className="flex-1">{r.label}</span>
                                    <span className="font-mono">
                                        {r.actual} / {r.limit} {r.unit}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {!ctx.border.auto_triggered && ctx.border.current !== 0 && (
                            <p className="text-xs text-slate-500 mt-2">
                                ავტომატური პირობა არ დაკმაყოფილებულა — სტატუსი ხელით
                                დაყენდა.
                            </p>
                        )}

                        {borderHistory === null ? (
                            <button
                                onClick={async () => {
                                    setLoadingHistory(true);
                                    try {
                                        const { data } = await axiosAdmin.get(
                                            `/packs/${pack.id}/borderHistory`
                                        );
                                        setBorderHistory(data);
                                    } catch (e) {
                                        errorToast(
                                            apiError(e, "ისტორია ვერ ჩაიტვირთა")
                                        );
                                        setBorderHistory([]);
                                    }
                                    setLoadingHistory(false);
                                }}
                                disabled={loadingHistory}
                                className="text-xs text-teal_600 hover:underline mt-2 disabled:opacity-50"
                            >
                                {loadingHistory
                                    ? "იტვირთება…"
                                    : "ვინ შეცვალა ხელით?"}
                            </button>
                        ) : borderHistory.length === 0 ? (
                            <p className="text-xs text-slate-400 mt-2">
                                ხელით არასდროს შეცვლილა — სტატუსი ავტომატურია.
                            </p>
                        ) : (
                            <div className="mt-2 flex flex-col gap-0.5">
                                {borderHistory.map((h, i) => (
                                    <p key={i} className="text-xs text-slate-500">
                                        <b>{h.admin_name || `admin #${h.admin_id}`}</b> →{" "}
                                        {h.label || h.set_to} ·{" "}
                                        {String(h.at).replace("T", " ").slice(0, 16)}
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Parcels that share this price. The per-kg rate comes from the
                    group's combined weight, so a sibling appearing or leaving
                    moves this parcel's price without anyone touching it. */}
                {ctx?.group?.packs?.length > 1 && (
                    <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 mb-3">
                        <p className="text-sm font-bold dark:text-white mb-1">
                            დაკავშირებული ამანათები — {ctx.group.shipment_code} · იუზერი #
                            {ctx.group.user_id}
                        </p>
                        <p className="text-xs text-slate-500 mb-2">
                            {ctx.group.count} ამანათი · ჯამი {ctx.group.sum_price} ₾ ·{" "}
                            {ctx.group.sum_weight} კგ — ფასი ჯგუფის მიხედვით ითვლება.
                        </p>
                        <div className="overflow-x-auto max-h-56 overflow-y-auto">
                            <table className="w-full text-xs">
                                <thead className="text-slate-500 sticky top-0 bg-white dark:bg-slate-800">
                                    <tr>
                                        <th className="text-left py-1 pr-2">თრექინგი</th>
                                        <th className="text-right py-1 pr-2">წონა</th>
                                        <th className="text-right py-1 pr-2">ღირებ.</th>
                                        <th className="text-right py-1 pr-2">ტრანსპ.</th>
                                        <th className="text-center py-1">განბ.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ctx.group.packs.map((p) => (
                                        <tr
                                            key={p.id}
                                            className={`border-t border-slate-100 dark:border-slate-700 ${
                                                p.is_this
                                                    ? "bg-teal-50 dark:bg-slate-700 font-bold"
                                                    : ""
                                            }`}
                                        >
                                            <td className="py-1 pr-2 font-mono dark:text-slate-200">
                                                {p.tracking_number}
                                                {p.is_this && (
                                                    <span className="text-teal_600"> ←</span>
                                                )}
                                            </td>
                                            <td className="py-1 pr-2 text-right dark:text-slate-200">
                                                {p.total_weight}
                                            </td>
                                            <td className="py-1 pr-2 text-right dark:text-slate-200">
                                                {p.total_price}
                                            </td>
                                            <td className="py-1 pr-2 text-right dark:text-slate-200">
                                                {p.transport_price}
                                            </td>
                                            <td className="py-1 text-center">
                                                <span
                                                    className={`inline-block w-2.5 h-2.5 rounded-full ${
                                                        p.border_declar === 1
                                                            ? "bg-red-500"
                                                            : p.border_declar === 2
                                                            ? "bg-green-500"
                                                            : p.border_declar === 3
                                                            ? "bg-yellow-400"
                                                            : "bg-slate-300"
                                                    }`}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Lock panel */}
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 mb-3">
                    <p className="text-sm font-bold dark:text-white mb-2">
                        {t("admin_price_lock") || "Price lock"}{" "}
                        {pack.price_locked ? (
                            <span className="text-red-500 ml-2">
                                LOCKED ({pack.price_lock_scope}) ={" "}
                                {fmt(pack.price_lock_amount)}
                            </span>
                        ) : (
                            <span className="text-green-500 ml-2">unlocked</span>
                        )}
                    </p>
                    {pack.price_lock_reason && (
                        <p className="text-xs text-slate-500 mb-2">
                            reason: {pack.price_lock_reason}
                        </p>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
                        <div>
                            <label className="block text-xs text-slate-500 mb-1">
                                {t("admin_amount") || "Amount"}
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={lockAmount}
                                onChange={(e) => setLockAmount(e.target.value)}
                                className="h-9 w-full px-2 border border-slate-300 dark:border-slate-600 rounded dark:bg-slate-700 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-slate-500 mb-1">
                                {t("admin_scope") || "Scope"}
                            </label>
                            <select
                                value={lockScope}
                                onChange={(e) => setLockScope(e.target.value)}
                                className="h-9 w-full px-2 border border-slate-300 dark:border-slate-600 rounded dark:bg-slate-700 dark:text-white"
                            >
                                <option value="pack">this pack</option>
                                <option value="user_shipment">user + shipment</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs text-slate-500 mb-1">
                                {t("admin_reason") || "Reason"}
                            </label>
                            <input
                                type="text"
                                value={lockReason}
                                onChange={(e) => setLockReason(e.target.value)}
                                className="h-9 w-full px-2 border border-slate-300 dark:border-slate-600 rounded dark:bg-slate-700 dark:text-white"
                            />
                        </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                        <button
                            onClick={handleLock}
                            className="bg-red-500 hover:bg-red-600 text-white text-sm px-3 h-9 rounded"
                        >
                            {pack.price_locked ? "Update lock" : "Lock"}
                        </button>
                        {pack.price_locked && (
                            <button
                                onClick={handleUnlock}
                                className="bg-green-500 hover:bg-green-600 text-white text-sm px-3 h-9 rounded"
                            >
                                Unlock & recalc
                            </button>
                        )}
                    </div>
                </div>

                {/* Current price breakdown panel — exact composition of the current bill */}
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 mb-3">
                    <p className="text-sm font-bold dark:text-white mb-2">
                        {t("admin_current_breakdown") ||
                            "მიმდინარე ფასის გათვლა / Current price breakdown"}
                    </p>

                    {!latestApplied ? (
                        <p className="text-slate-500 text-sm">
                            ჯერ არ ჩაწერილა გათვლა (No recalc has been applied yet
                            for this pack — final price ={" "}
                            <b>{fmt(pack.transport_price)}</b>).
                        </p>
                    ) : (
                        <>
                            {/* Inputs row */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-xs">
                                <div className="bg-slate-50 dark:bg-slate-900 rounded p-2">
                                    <p className="text-slate-500">
                                        ჯგუფის წონა / chargeable
                                    </p>
                                    <p className="font-mono dark:text-white">
                                        {fmt(latestApplied.inputs?.chargeable_weight)} კგ
                                        {latestApplied.inputs?.cal_price_by === 2
                                            ? " (volume)"
                                            : " (real)"}
                                    </p>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-900 rounded p-2">
                                    <p className="text-slate-500">
                                        per-kg USD →{" "}
                                        <span className="text-slate-400">
                                            {latestApplied.inputs?.per_kg_usd_source ||
                                                "—"}
                                        </span>
                                    </p>
                                    <p className="font-mono dark:text-white">
                                        ${fmt(latestApplied.inputs?.per_kg_usd)} / კგ
                                    </p>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-900 rounded p-2">
                                    <p className="text-slate-500">USD → GEL</p>
                                    <p className="font-mono dark:text-white">
                                        {fmt(latestApplied.inputs?.usd_to_gel)} ₾/$
                                    </p>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-900 rounded p-2">
                                    <p className="text-slate-500">per-kg GEL</p>
                                    <p className="font-mono dark:text-white">
                                        {fmt(latestApplied.inputs?.per_kg_gel)} ₾/კგ
                                    </p>
                                </div>
                            </div>

                            {/* Line items */}
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-left text-slate-500 border-b border-slate-300 dark:border-slate-600">
                                        <th className="py-1 pr-2">component</th>
                                        <th className="py-1 pr-2 text-right">amount</th>
                                        <th className="py-1 pr-2 text-right">% of final</th>
                                        <th className="py-1 pr-2">notes</th>
                                    </tr>
                                </thead>
                                <tbody className="dark:text-slate-200">
                                    <tr className="border-b border-slate-100 dark:border-slate-700">
                                        <td className="py-1 pr-2">
                                            საბაზო (weight × per-kg GEL)
                                        </td>
                                        <td className="py-1 pr-2 text-right font-mono">
                                            {signed(
                                                latestApplied.breakdown.base,
                                                false
                                            )}
                                        </td>
                                        <td className="py-1 pr-2 text-right text-slate-500">
                                            {pctOf(latestApplied.breakdown.base)}
                                        </td>
                                        <td className="py-1 pr-2 text-slate-500">
                                            {fmt(
                                                latestApplied.inputs?.chargeable_weight
                                            )}{" "}
                                            ×{" "}
                                            {fmt(latestApplied.inputs?.per_kg_gel)}{" "}
                                            ₾
                                        </td>
                                    </tr>

                                    {Number(
                                        latestApplied.breakdown.repack_uplift
                                    ) > 0 && (
                                        <tr className="border-b border-slate-100 dark:border-slate-700">
                                            <td className="py-1 pr-2">
                                                Repack (flat per pack)
                                            </td>
                                            <td className="py-1 pr-2 text-right font-mono">
                                                {signed(
                                                    latestApplied.breakdown
                                                        .repack_uplift,
                                                    false
                                                )}
                                            </td>
                                            <td className="py-1 pr-2 text-right text-slate-500">
                                                {pctOf(
                                                    latestApplied.breakdown
                                                        .repack_uplift
                                                )}
                                            </td>
                                            <td className="py-1 pr-2 text-slate-500">
                                                abroad_office.repack_price ×
                                                USD-rate
                                            </td>
                                        </tr>
                                    )}

                                    {Number(latestApplied.breakdown.insurance) >
                                        0 && (
                                        <tr className="border-b border-slate-100 dark:border-slate-700">
                                            <td className="py-1 pr-2">
                                                დაზღვევა / Insurance (10%)
                                            </td>
                                            <td className="py-1 pr-2 text-right font-mono">
                                                {signed(
                                                    latestApplied.breakdown.insurance,
                                                    false
                                                )}
                                            </td>
                                            <td className="py-1 pr-2 text-right text-slate-500">
                                                {pctOf(
                                                    latestApplied.breakdown.insurance
                                                )}
                                            </td>
                                            <td className="py-1 pr-2 text-slate-500">
                                                10% × total_price
                                            </td>
                                        </tr>
                                    )}

                                    <tr className="border-b border-slate-300 dark:border-slate-500 font-bold">
                                        <td className="py-1 pr-2">subtotal</td>
                                        <td className="py-1 pr-2 text-right font-mono">
                                            {fmt(latestApplied.breakdown.subtotal)}
                                        </td>
                                        <td className="py-1 pr-2 text-right text-slate-500">
                                            {pctOf(
                                                latestApplied.breakdown.subtotal
                                            )}
                                        </td>
                                        <td />
                                    </tr>

                                    {Number(latestApplied.breakdown.discount) >
                                        0 && (
                                        <tr className="border-b border-slate-100 dark:border-slate-700">
                                            <td className="py-1 pr-2">
                                                ფასდაკლება / Discount (
                                                {fmt(
                                                    latestApplied.breakdown
                                                        .discount_percent
                                                )}
                                                %)
                                            </td>
                                            <td className="py-1 pr-2 text-right font-mono text-green-600">
                                                {signed(
                                                    latestApplied.breakdown.discount,
                                                    true
                                                )}
                                            </td>
                                            <td className="py-1 pr-2 text-right text-slate-500">
                                                {pctOf(
                                                    latestApplied.breakdown.discount
                                                )}
                                            </td>
                                            <td className="py-1 pr-2 text-slate-500">
                                                {latestApplied.breakdown
                                                    .discount_source || "—"}
                                            </td>
                                        </tr>
                                    )}

                                    {Number(latestApplied.breakdown.promo_cut) >
                                        0 && (
                                        <tr className="border-b border-slate-100 dark:border-slate-700">
                                            <td className="py-1 pr-2">
                                                პრომოკოდი / Promo (
                                                {fmt(
                                                    latestApplied.breakdown
                                                        .promo_percent
                                                )}
                                                %)
                                            </td>
                                            <td className="py-1 pr-2 text-right font-mono text-green-600">
                                                {signed(
                                                    latestApplied.breakdown.promo_cut,
                                                    true
                                                )}
                                            </td>
                                            <td className="py-1 pr-2 text-right text-slate-500">
                                                {pctOf(
                                                    latestApplied.breakdown.promo_cut
                                                )}
                                            </td>
                                            <td className="py-1 pr-2 text-slate-500">
                                                {latestApplied.breakdown
                                                    .promo_code || "—"}
                                            </td>
                                        </tr>
                                    )}

                                    {Number(latestApplied.breakdown.inner_price) >
                                        0 && (
                                        <tr className="border-b border-slate-100 dark:border-slate-700">
                                            <td className="py-1 pr-2">
                                                შიდა საფასური / inner_price
                                            </td>
                                            <td className="py-1 pr-2 text-right font-mono">
                                                {signed(
                                                    latestApplied.breakdown
                                                        .inner_price,
                                                    false
                                                )}
                                            </td>
                                            <td className="py-1 pr-2 text-right text-slate-500">
                                                {pctOf(
                                                    latestApplied.breakdown
                                                        .inner_price
                                                )}
                                            </td>
                                            <td className="py-1 pr-2 text-slate-500">
                                                manual addition
                                            </td>
                                        </tr>
                                    )}

                                    {Number(latestApplied.breakdown.fine_price) >
                                        0 && (
                                        <tr className="border-b border-slate-100 dark:border-slate-700">
                                            <td className="py-1 pr-2">
                                                ჯარიმა / fine
                                            </td>
                                            <td className="py-1 pr-2 text-right font-mono">
                                                {signed(
                                                    latestApplied.breakdown
                                                        .fine_price,
                                                    false
                                                )}
                                            </td>
                                            <td className="py-1 pr-2 text-right text-slate-500">
                                                {pctOf(
                                                    latestApplied.breakdown
                                                        .fine_price
                                                )}
                                            </td>
                                            <td className="py-1 pr-2 text-slate-500">
                                                penalty
                                            </td>
                                        </tr>
                                    )}

                                    <tr className="font-bold text-base">
                                        <td className="py-2 pr-2">
                                            FINAL (transport_price)
                                        </td>
                                        <td className="py-2 pr-2 text-right font-mono dark:text-white">
                                            {fmt(latestApplied.breakdown.final)} ₾
                                        </td>
                                        <td className="py-2 pr-2 text-right text-slate-400">
                                            100%
                                        </td>
                                        <td />
                                    </tr>
                                </tbody>
                            </table>

                            {/* Last-applied metadata */}
                            <p className="text-[11px] text-slate-500 mt-2">
                                ბოლო გათვლა:{" "}
                                <span className="font-mono">
                                    {fmtDate(latestApplied.created_at)}
                                </span>{" "}
                                · trigger:{" "}
                                <span className="font-mono">
                                    {latestApplied.trigger}
                                </span>{" "}
                                · by{" "}
                                {latestApplied.actor_admin_name ||
                                    (latestApplied.actor_admin_id
                                        ? `admin#${latestApplied.actor_admin_id}`
                                        : latestApplied.actor_user_id
                                        ? `user#${latestApplied.actor_user_id}`
                                        : latestApplied.source)}
                            </p>

                            {lastCapped &&
                                new Date(lastCapped.created_at) >
                                    new Date(latestApplied.created_at) && (
                                    <div className="mt-3 p-2 rounded bg-yellow-50 dark:bg-yellow-900/30 text-xs">
                                        <p className="font-bold text-yellow-700 dark:text-yellow-300">
                                            ⚠ ფასი გადიდდებოდა, მაგრამ შეჩერდა
                                            (no_increase_capped)
                                        </p>
                                        <p className="text-yellow-700 dark:text-yellow-300">
                                            გათვლის შედეგი:{" "}
                                            <b>
                                                {fmt(
                                                    lastCapped.inputs?.computed_price
                                                )}{" "}
                                                ₾
                                            </b>
                                            ; შენახული:{" "}
                                            <b>
                                                {fmt(
                                                    lastCapped.inputs?.kept_price
                                                )}{" "}
                                                ₾
                                            </b>{" "}
                                            ({fmtDate(lastCapped.created_at)},{" "}
                                            {lastCapped.trigger})
                                        </p>
                                    </div>
                                )}
                        </>
                    )}
                </div>

                {/* Manual recalc panel */}
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 mb-3">
                    <p className="text-sm font-bold dark:text-white mb-2">
                        {t("admin_manual_recalculate") || "Manual recalculate"}
                    </p>
                    <div className="flex flex-wrap gap-2 items-end">
                        <div>
                            <label className="block text-xs text-slate-500 mb-1">
                                Override per-kg USD (optional)
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={overrideKg}
                                onChange={(e) => setOverrideKg(e.target.value)}
                                className="h-9 w-44 px-2 border border-slate-300 dark:border-slate-600 rounded dark:bg-slate-700 dark:text-white"
                            />
                        </div>
                        <label className="flex items-center gap-2 text-sm dark:text-white pb-1">
                            <input
                                type="checkbox"
                                checked={recalcGroup}
                                onChange={(e) => setRecalcGroup(e.target.checked)}
                            />
                            recalc whole user + shipment group
                        </label>
                        <button
                            onClick={handleRecalculate}
                            className="bg-blue-500 hover:bg-blue-600 text-white text-sm px-3 h-9 rounded"
                        >
                            Recalculate
                        </button>
                    </div>
                </div>

                {/* Log table */}
                <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                    <p className="text-sm font-bold dark:text-white mb-2">
                        {t("admin_recalculation_log") || "Recalculation log"}
                    </p>
                    {loading && logs.length === 0 ? (
                        <p className="text-slate-500 text-sm">loading…</p>
                    ) : logs.length === 0 ? (
                        <p className="text-slate-500 text-sm">no entries</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-left text-slate-500 border-b border-slate-300 dark:border-slate-600">
                                        <th className="py-1 pr-2">when</th>
                                        <th className="py-1 pr-2">trigger</th>
                                        <th className="py-1 pr-2">actor</th>
                                        <th className="py-1 pr-2">old → new</th>
                                        <th className="py-1 pr-2">Δ</th>
                                        <th className="py-1 pr-2">status</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((row) => (
                                        <Fragment key={row.id}>
                                            <tr
                                                className="border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
                                            >
                                                <td className="py-1 pr-2 dark:text-slate-300">
                                                    {fmtDate(row.created_at)}
                                                </td>
                                                <td className="py-1 pr-2 dark:text-slate-300 font-mono">
                                                    {row.trigger}
                                                </td>
                                                <td className="py-1 pr-2 dark:text-slate-300">
                                                    {row.source}
                                                    {row.actor_admin_name
                                                        ? ` · ${row.actor_admin_name}`
                                                        : row.actor_admin_id
                                                        ? ` · admin#${row.actor_admin_id}`
                                                        : row.actor_user_id
                                                        ? ` · user#${row.actor_user_id}`
                                                        : ""}
                                                </td>
                                                <td className="py-1 pr-2 dark:text-slate-300">
                                                    {fmt(row.old_price)} → {fmt(row.new_price)}
                                                </td>
                                                <td
                                                    className={`py-1 pr-2 font-bold ${
                                                        Number(row.delta) > 0
                                                            ? "text-red-500"
                                                            : Number(row.delta) < 0
                                                            ? "text-green-500"
                                                            : "text-slate-400"
                                                    }`}
                                                >
                                                    {row.delta > 0 ? "+" : ""}
                                                    {fmt(row.delta)}
                                                </td>
                                                <td className="py-1 pr-2">
                                                    {row.skipped ? (
                                                        <span className="text-yellow-500">
                                                            skipped: {row.skip_reason}
                                                        </span>
                                                    ) : (
                                                        <span className="text-green-500">
                                                            applied
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-1">
                                                    <button
                                                        onClick={() =>
                                                            setExpanded(
                                                                expanded === row.id
                                                                    ? null
                                                                    : row.id
                                                            )
                                                        }
                                                        className="text-blue-500 underline"
                                                    >
                                                        {expanded === row.id
                                                            ? "hide"
                                                            : "details"}
                                                    </button>
                                                </td>
                                            </tr>
                                            {expanded === row.id && (
                                                <tr>
                                                    <td
                                                        colSpan={7}
                                                        className="bg-slate-50 dark:bg-slate-900 p-2"
                                                    >
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs dark:text-slate-300">
                                                            <pre className="overflow-auto bg-white dark:bg-slate-800 p-2 rounded">
inputs:{"\n"}{JSON.stringify(row.inputs, null, 2)}
                                                            </pre>
                                                            <pre className="overflow-auto bg-white dark:bg-slate-800 p-2 rounded">
breakdown:{"\n"}{JSON.stringify(row.breakdown, null, 2)}
                                                            </pre>
                                                            <pre className="overflow-auto bg-white dark:bg-slate-800 p-2 rounded">
before → after:{"\n"}{JSON.stringify(
                                                                    {
                                                                        before: row.before,
                                                                        after: row.after,
                                                                    },
                                                                    null,
                                                                    2
                                                                )}
                                                            </pre>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    ))}
                                </tbody>
                            </table>
                            {hasMore && (
                                <button
                                    onClick={() => fetchLogs(page + 1)}
                                    className="mt-2 text-blue-500 underline text-xs"
                                >
                                    load more
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PriceAuditModal;
